import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Children, Fragment, isValidElement, useState, type ReactNode } from 'react';

/**
 * Zugängliches Aktionsmenü (Tastatur, Screenreader, Touch) – der Weg ohne Ziehen für jede Ziehaktion.
 * Leistung (P5-Messung): Listen haben hunderte Zeilen mit je einem Menü. Bis zum ersten Öffnen ist das Menü nur ein
 * einfacher Knopf; erst beim ersten Klick (Maus, Touch, Enter/Leertaste) wird das Radix-Menü aufgebaut.
 */
/** `text`: Auslöser mit sichtbarer Beschriftung – Breite nach Inhalt, Mindesthöhe 44 px (Nachprüfung R1) */
export function Menu({ label, trigger, children, small, text }: { label: string; trigger: ReactNode; children: ReactNode | (() => ReactNode); small?: boolean; text?: boolean }) {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const button = (
    <button
      type="button"
      aria-label={label}
      aria-haspopup="menu"
      className={`${text ? 'inline-flex min-h-11 items-center whitespace-nowrap border border-zinc-300 px-3 text-zinc-800 dark:border-zinc-700 dark:text-zinc-200' : `grid ${small ? 'size-9' : 'size-11'} place-items-center text-zinc-500`} shrink-0 rounded-lg hover:bg-zinc-100 data-[state=open]:bg-zinc-100 dark:hover:bg-zinc-800 dark:data-[state=open]:bg-zinc-800`}
      // Ziehen und Zeilen-Tastatur nicht auslösen
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      onClick={
        mounted
          ? undefined
          : () => {
              setMounted(true);
              setOpen(true);
            }
      }
    >
      {trigger}
    </button>
  );
  if (!mounted) return button;
  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>{button}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          // Eine Menüaktion hat schon etwas anderes fokussiert (Eingabefeld, Auswahlfenster)? Dann den Fokus nicht
          // zurück auf den Auslöser holen – sonst schließt sich das eben geöffnete Feld auf langsamen Geräten wieder.
          onCloseAutoFocus={(e) => {
            const a = document.activeElement;
            if (a && a !== document.body && !a.closest('[role="menu"]') && !a.closest('[aria-haspopup="menu"]')) e.preventDefault();
          }}
          align="end"
          sideOffset={4}
          collisionPadding={8}
          className="menu-pop z-50 max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-52 max-w-[calc(100vw-1rem)] overflow-y-auto overscroll-contain rounded-xl border border-zinc-200 bg-white p-1 text-sm shadow-lg dark:border-zinc-800 dark:bg-zinc-900"
        >
          {/* Als Funktion: Inhalt wird erst beim Öffnen mit aktuellem Stand berechnet */}
          {open && (typeof children === 'function' ? children() : children)}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/**
 * `hint`: kurze Erklärung unter dem Eintrag – vor allem, warum er gerade nicht geht (0.150, Entwicklerbericht B:
 * „fachliche Blockaden erklären statt unerklärt deaktivieren“; ein Tooltip allein reicht am Handy nicht).
 */
export function MenuItem({ onSelect, children, danger, disabled, hint, ...data }: { onSelect: () => void; children: ReactNode; danger?: boolean; disabled?: boolean; hint?: string } & { [k: `data-${string}`]: string | undefined }) {
  return (
    <DropdownMenu.Item
      disabled={disabled}
      onSelect={onSelect}
      {...data}
      className={`flex min-h-10 cursor-pointer items-center gap-2 rounded-lg px-3 outline-none data-[disabled]:cursor-default data-[highlighted]:bg-zinc-100 dark:data-[highlighted]:bg-zinc-800 ${hint ? 'py-1' : ''} ${disabled && !hint ? 'data-[disabled]:opacity-40' : ''} ${
        danger ? 'text-red-700 dark:text-red-400' : ''
      }`}
    >
      {hint ? (
        <span className="flex min-w-0 flex-1 flex-col">
          <span className={`flex items-center gap-2 ${disabled ? 'opacity-50' : ''}`}>{children}</span>
          <span className="text-xs text-zinc-500">{hint}</span>
        </span>
      ) : (
        children
      )}
    </DropdownMenu.Item>
  );
}

/** Überschrift einer Menügruppe (Projektmenü) – ruhig, nicht anwählbar */
export const MenuLabel = ({ children }: { children: ReactNode }) => <DropdownMenu.Label className="px-3 pt-2 pb-1 text-xs font-medium text-zinc-500">{children}</DropdownMenu.Label>;

/**
 * 0.150 (ADR 0152): Menü aus Gruppen. Leere Gruppen verschwinden samt Trennlinie – nie verwaiste Trenner, egal welche
 * Rechte welche Einträge ausblenden. Jede Gruppe ist ein Fragment/Array; `false`/`null` zählen nicht.
 * Kontextmenüs folgen der Reihenfolge 1 Öffnen/Bearbeiten · 2 Organisieren · 3 Zusammenarbeiten ·
 * 4 Weiterverwenden/Nachsehen · 5 Entfernen (abgesetzt am Ende).
 */
export function MenuGroups({ groups }: { groups: { key: string; label?: string; items: ReactNode }[] }) {
  const filled = groups.filter((g) => flat(g.items).length > 0);
  return (
    <>
      {filled.map((g, i) => (
        <Fragment key={g.key}>
          {i > 0 && <MenuSeparator />}
          {g.label && <MenuLabel>{g.label}</MenuLabel>}
          <DropdownMenu.Group data-menu-group={g.key}>{g.items}</DropdownMenu.Group>
        </Fragment>
      ))}
    </>
  );
}

/** sichtbare Kinder (Fragmente aufgelöst, false/null/'' entfernt) */
function flat(node: ReactNode): ReactNode[] {
  const out: ReactNode[] = [];
  Children.forEach(node, (c) => {
    if (c === null || c === undefined || c === false || c === true || c === '') return;
    if (isValidElement<{ children?: ReactNode }>(c) && c.type === Fragment) out.push(...flat(c.props.children));
    else out.push(c);
  });
  return out;
}

export const MenuSeparator = () => <DropdownMenu.Separator className="my-1 h-px bg-zinc-100 dark:bg-zinc-800" />;
